import {match} from 'ts-pattern';

import {BranchResponseBody} from '@dms/api/shared';

import {Nullish} from 'shared/web';

import {getValidatedBankAccount} from './getValidatedBankAccount';

/**
 * @public
 * High-level utility for getting default bank account from branch data.
 * Use this in components instead of lower-level utilities.
 * Gets the default bank account from the branch data, validating it against the available bank accounts.
 * @param originBankAccount
 * @param branchData
 * @param isSynced
 * @param selectFirstIfNoPrimary
 * @returns An object containing the selected bank account and the list of available bank accounts
 *
 * The function first checks if the branch data is synced. If it is, it retrieves the bank accounts from the branch's billing information. It then validates the origin bank account against the available bank accounts. If the origin bank account is valid, it is selected. If not, it falls back to selecting the primary bank account or the first account if selectFirstIfNoPrimary is true.
 */
export const getDefaultBankAccountFromBranch = (
  originBankAccount: string | Nullish,
  branchData: BranchResponseBody | Nullish,
  isSynced: boolean,
  selectFirstIfNoPrimary = true
) => {
  const availableBankAccounts = match(isSynced)
    .with(true, () => branchData?.billingInformation?.bankAccounts ?? [])
    .otherwise(() => []);

  const selectedBankAccount = getValidatedBankAccount(
    originBankAccount,
    availableBankAccounts,
    selectFirstIfNoPrimary
  );

  return {
    selectedBankAccount,
    availableBankAccounts,
  };
};
